import { Buffer } from 'buffer';

import {
  EQueryKeys,
  TSessionStorageInput,
  TSessionStorageOutput,
  USessionStorageKeys,
} from '../../../types';

export const base64Encoder = (value: string): string | null => {
  try {
    const input = Buffer.from(value, 'utf8');
    return input.toString('base64');
  } catch (err) {
    console.error('base64Encoder [ERROR]\n', err);
    return null;
  }
};

export const base64Decoder = (value: string): string | null => {
  try {
    const input = Buffer.from(value, 'base64');
    return input.toString('utf8');
  } catch (err) {
    console.error('base64Decoder [ERROR]\n', err);
    return null;
  }
};

export const UserException = (message: string) => {
  return {
    name: 'CustomException',
    message: message,
  };
};

/** Hosts that may receive Cognito tokens for an MCP login. Web and mobile redirects are unchanged. */
export const MCP_CALLBACK_HOSTS = [
  'dev-api-v2.tripplo.co',
  'staging-api-v2.tripplo.co',
  'prod-api-v2.tripplo.co',
];

export const isAllowedMcpRedirect = (redirectUri: string): boolean => {
  try {
    const url = new URL(redirectUri);
    const pathOk = url.pathname.endsWith('/oauth/callback');
    const knownHost = MCP_CALLBACK_HOSTS.includes(url.hostname);
    const executeApiHost = url.hostname.endsWith('.execute-api.eu-west-1.amazonaws.com');
    return pathOk && (knownHost || executeApiHost);
  } catch {
    return false;
  }
};

export const allowedUserType = async (
  jwtToken: string,
  appType: string | null | undefined
): Promise<boolean> => {
  try {
    if (appType === 'web' || appType === 'mcp') return true;

    const splitJWT = jwtToken.split('.');
    let processedJWT: any = base64Decoder(splitJWT[1]);

    if (!processedJWT) throw new Error('Unable to process JWT');

    processedJWT = JSON.parse(processedJWT);

    if (!processedJWT) throw new Error('Unable to parse JWT');

    const group = processedJWT['cognito:groups']?.[0];
    if (!group) throw new Error('Unable to retrieve JWT group');

    const allowedTypes = ['Transporter', 'TransBroker', 'Admin', 'Driver'];

    return allowedTypes.includes(group);
  } catch (err) {
    console.error('allowedUserType [ERROR]\n', err);
    return false;
  }
};

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const uniformTimestamp = (): number | null => {
  try {
    return Math.trunc(new Date().getTime() / 1000);
  } catch {
    return null;
  }
};

export const hasSurpassedTimeLimit = (
  timestamp: number,
  timeLimit: number
): boolean | null => {
  try {
    const limitTimestamp = timestamp + timeLimit;
    const currentTime = uniformTimestamp();

    if (currentTime === null) return false;
    return currentTime > limitTimestamp;
  } catch (err) {
    console.error('IS-VALID-TIME-ERROR:\n', err);
    return null;
  }
};

export const setSessionTimestamp = (key: string): boolean => {
  try {
    const currentTime = uniformTimestamp();
    if (currentTime === null) return false;

    sessionStorage.setItem(key, JSON.stringify(currentTime));
    return true;
  } catch (err) {
    console.error('SET-SESSION-TIMESTAMP-ERROR:\n', err);
    return false;
  }
};

type EvalSessTimestampResult = {
  hasExisted: boolean;
  timestamp: number;
};

export const evaluateSessionTimestamp = (
  key: string
): EvalSessTimestampResult | null => {
  try {
    const result: EvalSessTimestampResult = {
      hasExisted: false,
      timestamp: 0,
    };

    const timestamp = sessionStorage.getItem(key);

    if (timestamp === null) {
      const didSet = setSessionTimestamp(key);
      const currentTime = uniformTimestamp();

      if (currentTime === null) throw new Error('Unable to retrieve current time.');
      result.timestamp = currentTime;

      if (!didSet) throw new Error('Unable to set session timestamp.');
      return result;
    }

    result.hasExisted = true;
    result.timestamp = JSON.parse(timestamp);

    return result;
  } catch (err) {
    console.error('EVALUATE-SESSION-TIMESTAMP-ERROR:\n', err);
    return null;
  }
};

export const populateSessionStorage = (
  input: TSessionStorageInput[]
): boolean => {
  try {
    if (input.length === 0) {
      throw new Error('populateSessionStorage input array is empty.');
    }

    for (const inputPair of input) {
      sessionStorage.setItem(inputPair.key, inputPair.value);
    }

    return true;
  } catch (err) {
    console.error('POPULATE-SESSION-STORAGE-ERROR:\n', err);
    return false;
  }
};

type TImprovisedRetrieveSessionStorage = {
  [EQueryKeys.APP_TYPE]: string | null;
  [EQueryKeys.ROUTE_TYPE]: string | null;
  [EQueryKeys.REDIRECT_URI]: string | null;
};

type IRetrieveSessionStorageOutput = TImprovisedRetrieveSessionStorage &
  TSessionStorageOutput;

export const retrieveSessionStorage = (
  input: USessionStorageKeys[]
): IRetrieveSessionStorageOutput | null => {
  try {
    const retrieveResult: IRetrieveSessionStorageOutput = {
      auth_token: null,
      refresh_token: null,
      id_token: null,
      org_id: null,
      [EQueryKeys.APP_TYPE]: null,
      [EQueryKeys.ROUTE_TYPE]: null,
      [EQueryKeys.REDIRECT_URI]: null,
    };

    if (input.length === 0) {
      throw new Error('retrieveSessionStorage input array is empty.');
    }

    for (const key of input) {
      const value = sessionStorage.getItem(key);
      (retrieveResult as any)[key] = value;
    }

    return retrieveResult;
  } catch (err) {
    console.error('RETRIEVE-SESSION-STORAGE-ERROR:\n', err);
    return null;
  }
};

export const clearSessionStorage = (input: USessionStorageKeys[]): boolean => {
  try {
    if (input.length === 0) {
      throw new Error('clearSessionStorage input array is empty.');
    }

    for (const key of input) {
      sessionStorage.removeItem(key);
    }

    return true;
  } catch (err) {
    console.error('CLEAR-SESSION-STORAGE-ERROR:\n', err);
    return false;
  }
};

type ParsedInviteMemberUrlOutput = {
  email: string;
  inviteId: string;
};

export const parseInviteMemberUrl = (
  pathname: string
): ParsedInviteMemberUrlOutput | null => {
  try {
    const pathnameArr = pathname.split('/');
    const pathEmail = pathnameArr[2];
    const pathInviteId = pathnameArr[3];

    if (!pathEmail || !pathInviteId)
      throw new Error('parseInviteMemberUrl - Pathname array values are empty');

    return {
      email: pathEmail,
      inviteId: pathInviteId,
    };
  } catch (err) {
    console.error('PARSE-INVITE-URL-ERROR:\n', err);
    return null;
  }
};

export const dateToEpoch = (dateString: string | Date): number | null => {
  try {
    return Math.trunc(new Date(dateString).getTime() / 1000);
  } catch (err) {
    console.error('DATE-TO-EPOCH-ERROR:\n', err);
    return null;
  }
};

export const checkObject = (data: object, keys: string[]): boolean => {
  return keys.every(
    (key) => (data as any)?.[key] !== null && (data as any)?.[key] !== undefined
  );
};

type IRequestInit<D> = Omit<RequestInit, 'body'> & {
  responseType?: 'json' | 'text' | 'arraybuffer';
  body?: D;
};

type IResponseBody<T> = {
  data: T | null;
  error?: string;
};

export const request = async <T = void, D = undefined | BodyInit>(
  uri: string,
  option: IRequestInit<D>
): Promise<IResponseBody<T>> => {
  try {
    const responseType = option.responseType || 'json';
    const $option: Omit<RequestInit, 'responseType'> = {
      ...option,
      body: option.body ? JSON.stringify(option.body) : undefined,
    };
    const response = await fetch(uri, $option);
    const data = await (response as any)[responseType]();
    if (response.status >= 200 && response.status < 226) {
      return { data: data as T };
    }
    throw new Error(data);
  } catch (error: any) {
    console.error(`Request Error ${uri}`, error);
    return { data: null, error: error.message };
  }
};
